import React, { useEffect, useState } from "react";

import {
  addShapeProcessor,
  checkValidShapefileSize,
  checkValidShapefileType,
  handleFileUpload,
  processGeometries,
  processGeometriesCombined,
  processGeometriesPL,
} from "utils";

import { COUNTRY_CODE } from "consts";

import { AdminHead, AdminSection, Button, Card, Col, Flex, MainPage, MapInteractive, Radio, Row, TagNormal, Upload, Message as message } from "components";

export const ShapeFiles = () => {
  const [loading, setLoading] = useState(false);
  const [geometries, setGeometries] = useState([]);
  const [location, setLocation] = useState({
    lat: 52.5224832,
    lng: 13.3906564,
  });

  const [country, setCountry] = useState(COUNTRY_CODE.GERMANY);

  const onError = (error) => {
    message({
      content: `File processing failed with the error ${error}.`,
      icon: "gremlin",
    });
  };

  const processShapes = async (data) => {
    setLocation(false);
    // if country is poland, use processGeometriesPL, if country is all, use processGeometriesCombined, else use processGeometries
    // I also don't like these multi-level ternary operators, but I will leave it as-is for now
    const processedGeometries =
      country === COUNTRY_CODE.POLAND
        ? await processGeometriesPL(data, onError)
        : country === "ALL"
        ? await processGeometriesCombined(data, onError)
        : await processGeometries(data, onError);
    setGeometries(processedGeometries);
    setLocation({
      lat: processedGeometries[0].geometry[0].lat,
      lng: processedGeometries[0].geometry[0].lng,
    });
  };

  const processShapeFile = (file) => {
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    reader.onabort = () =>
      message({
        content: "File reading was aborted unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onerror = () =>
      message({
        content: "File reading was failed unexpectedly. Try again.",
        icon: "gremlin",
      });

    reader.onloadend = async () => {
      const binaryStr = reader.result;

      if (fileType === "zip") {
        processShapes(binaryStr);
      } else {
        message({
          content: "You must upload a valid zip file with shp, prj and dbf files included.",
          icon: "gremlin",
        });
      }
    };
    if (fileType === "zip") {
      reader.readAsArrayBuffer(file);
    } else {
      message({
        content: "You must upload a valid zip file with shp, prj and dbf files included.",
        icon: "gremlin",
      });
    }
  };

  const handleUpload = (file) => {
    handleFileUpload({
      file: file,
      location: "shapefiles",
    })
      .then(() => {
        processShapeFile(file);
        message({
          content: "Your file was accepted by the gremlins.",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fileUpload = async (file) => {
    const isValidType = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!isValidType) {
      message({
        content: "Gremlins only eat valid zip files.",
        icon: "gremlin",
      });
    } else if (!isValidSize) {
      message({
        content: "Your file was too large for the gremlins.",
        icon: "gremlin",
      });
    } else {
      handleUpload(file);
    }
  };

  useEffect(() => {
    addShapeProcessor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline={
          <>
            Shapefiles <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description="Upload a zip file that contains a .dbf file AND a .shp file. The output of the shapefiles will be shown on the map in red."
        extra={
          <>
            <Upload name="file" showUploadList={false} action="" beforeUpload={(v) => fileUpload(v)} className="auto-height">
              <Button type="primary">Upload zip</Button>
            </Upload>
          </>
        }
      />

      <Flex justify="center" align="center" className="margin-bottom-200">
        <Radio.Group defaultValue={country}>
          {[
            { label: "Poland", value: COUNTRY_CODE.POLAND },
            { label: "Germany", value: COUNTRY_CODE.GERMANY },
            { label: "Does not matter", value: "ALL" },
          ].map((item) => (
            <Radio value={item.value} key={item.value} onChange={(e) => setCountry(e.target.value)}>
              {item.label}
            </Radio>
          ))}
        </Radio.Group>
      </Flex>

      {!loading && (
        <div className="content">
          {location && (
            <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
              <Col>
                <Card className="map-card">
                  <MapInteractive
                    defaultZoom={11}
                    geometries={geometries.map((geometry) => ({
                      ...geometry,
                      type: "shapeFile",
                    }))}
                    location={location}
                    markers={geometries.map((geometry) => ({
                      location: geometry.center,
                      size: geometries.length > 10 ? "m" : "l",
                    }))}
                    scaleControl={true}
                    zoomControl={true}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <AdminSection headline="Shapefile output" />
          <Row>
            <Col>
              {geometries.length
                ? geometries.map((geometry, i) => (
                    <p className="paragraph-s" key={i}>
                      <b>Field {i}</b>
                      <br />
                      {geometry.geometry.length} polygon vertices
                      <br />
                      {JSON.stringify(geometry.field)}
                      <br />
                      <b>Center of the polygon</b>
                      {JSON.stringify(geometry.center)}
                      <br />
                      <br />
                    </p>
                  ))
                : null}
            </Col>
          </Row>
        </div>
      )}
    </MainPage>
  );
};
